import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import { getCurrencySymbol } from '../utils/currency';
import TagSelector from '../components/TagSelector';
import './Customers.css';
import './Cars.css';

function Customers({ tenantId }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [cars, setCars] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [expandedCars, setExpandedCars] = useState(new Set());
  const [expandedJournals, setExpandedJournals] = useState(new Set());
  const [expandedCustomers, setExpandedCustomers] = useState(new Set());
  const [showForm, setShowForm] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(null);
  const [noteFormData, setNoteFormData] = useState({ note: '', mileage: '' });
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [tags, setTags] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      zip: '',
      country: ''
    },
    tags: []
  });

  useEffect(() => {
    if (tenantId) {
      loadData();
    }
  }, [tenantId]);

  const loadData = async () => {
    await Promise.all([
      loadTenant(),
      loadCustomers(),
      loadCars(),
      loadRepairs(),
      loadTags()
    ]);
  };

  const loadTags = async () => {
    try {
      const data = await api.get('/tags', tenantId);
      setTags(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading tags:', error);
      setTags([]);
    }
  };

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadCustomers = async () => {
    try {
      if (!tenantId) {
        console.error('No tenantId provided');
        return;
      }
      const data = await api.get('/customers', tenantId);
      const customersData = Array.isArray(data) ? data : [];
      setCustomers(customersData);
      setFilteredCustomers(customersData);
    } catch (error) {
      console.error('Error loading customers:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Fehler beim Laden der Kunden';
      console.error('Error details:', errorMessage);
      setCustomers([]);
      setFilteredCustomers([]);
    }
  };

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCustomers(customers);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = customers.filter(customer => 
        (customer.firstName && customer.firstName.toLowerCase().includes(query)) ||
        (customer.lastName && customer.lastName.toLowerCase().includes(query)) ||
        (customer.email && customer.email.toLowerCase().includes(query)) ||
        (customer.phone && customer.phone.toLowerCase().includes(query)) ||
        (customer.address?.street && customer.address.street.toLowerCase().includes(query)) ||
        (customer.address?.city && customer.address.city.toLowerCase().includes(query)) ||
        (customer.address?.zip && customer.address.zip.toLowerCase().includes(query))
      );
      setFilteredCustomers(filtered);
    }
  }, [searchQuery, customers]);

  const loadCars = async () => {
    try {
      const data = await api.get('/cars', tenantId);
      setCars(data);
    } catch (error) {
      console.error('Error loading cars:', error);
    }
  };

  const loadRepairs = async () => {
    try {
      const data = await api.get('/repairs', tenantId);
      setRepairs(data);
    } catch (error) {
      console.error('Error loading repairs:', error);
    }
  };

  const getCarsForCustomer = (customerId) => {
    return cars.filter(car => car.customerId === customerId);
  };

  const getRepairsForCar = (carId) => {
    return repairs
      .filter(repair => repair.carId === carId)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const toggleCarHistory = (carId) => {
    const newExpanded = new Set(expandedCars);
    if (newExpanded.has(carId)) {
      newExpanded.delete(carId);
    } else {
      newExpanded.add(carId);
    }
    setExpandedCars(newExpanded);
  };

  const toggleJournal = (carId) => {
    const newExpanded = new Set(expandedJournals);
    if (newExpanded.has(carId)) {
      newExpanded.delete(carId);
    } else {
      newExpanded.add(carId);
    }
    setExpandedJournals(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const toggleCustomerCars = (customerId) => {
    const newExpanded = new Set(expandedCustomers);
    if (newExpanded.has(customerId)) {
      newExpanded.delete(customerId);
    } else {
      newExpanded.add(customerId);
    }
    setExpandedCustomers(newExpanded);
  };

  const handleAddNote = async (carId) => {
    try {
      await api.post(`/cars/${carId}/notes`, noteFormData, tenantId);
      setNoteFormData({ note: '', mileage: '' });
      setShowNoteForm(null);
      await loadCars();
    } catch (error) {
      console.error('Error adding note:', error);
      showNotification('Fehler beim Hinzufügen der Notiz', 'error');
    }
  };

  const handleDeleteNote = async (carId, noteId) => {
    if (window.confirm('Notiz wirklich löschen?')) {
      try {
        await api.delete(`/cars/${carId}/notes/${noteId}`, tenantId);
        await loadCars();
      } catch (error) {
        console.error('Error deleting note:', error);
        showNotification('Fehler beim Löschen der Notiz', 'error');
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Ausstehend', class: 'status-pending' },
      in_progress: { label: 'In Bearbeitung', class: 'status-in_progress' },
      completed: { label: 'Abgeschlossen', class: 'status-completed' }
    };
    const statusInfo = statusMap[status] || { label: status, class: 'status-pending' };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!tenantId) {
        showNotification('Fehler: Keine Tenant-ID vorhanden', 'error');
        return;
      }
      
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, formData, tenantId);
      } else {
        await api.post('/customers', formData, tenantId);
      }
      setShowForm(false);
      setEditingCustomer(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: { street: '', city: '', zip: '', country: '' }
      });
      loadCustomers();
    } catch (error) {
      console.error('Error saving customer:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Fehler beim Speichern des Kunden';
      showNotification(`Fehler: ${errorMessage}`, 'error');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || {
        street: '',
        city: '',
        zip: '',
        country: ''
      },
      tags: customer.tags || []
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Kunden wirklich löschen?')) {
      try {
        await api.delete(`/customers/${id}`, tenantId);
        loadCustomers();
      } catch (error) {
        console.error('Error deleting customer:', error);
      }
    }
  };

  return (
    <div className="customers">
      <div className="page-header">
        <h1>Kunden</h1>
        <button type="button" onClick={() => setShowForm(true)} className="btn-primary">
          Neuer Kunde
        </button>
      </div>

      {showForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>{editingCustomer ? 'Kunde bearbeiten' : 'Neuer Kunde'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Vorname</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Nachname</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>E-Mail</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Telefon</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Straße</label>
                <input
                  type="text"
                  value={formData.address.street}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, street: e.target.value }
                  })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>PLZ</label>
                  <input
                    type="text"
                    value={formData.address.zip}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, zip: e.target.value }
                    })}
                  />
                </div>
                <div className="form-group">
                  <label>Stadt</label>
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, city: e.target.value }
                    })}
                  />
                </div>
              </div>
              <div className="form-group">
                <TagSelector
                  tenantId={tenantId}
                  selectedTags={formData.tags || []}
                  onChange={(tags) => setFormData({ ...formData, tags })}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingCustomer ? 'Speichern' : 'Erstellen'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingCustomer(null);
                  }}
                  className="btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="page-header" style={{ marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="Kunden suchen..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '0.9rem',
            minWidth: '300px'
          }}
        />
      </div>

      <div className="customers-list">
        {filteredCustomers.map(customer => {
          const customerCars = getCarsForCustomer(customer.id);
          return (
            <div key={customer.id} className="customer-card">
              <div className="customer-header">
                <div className="customer-info">
                  <h2>
                    <button 
                      className="link-button" 
                      onClick={() => navigate(`/customers/${customer.id}`)}
                      style={{ fontSize: '1.2em', fontWeight: 'bold' }}
                    >
                      {customer.firstName} {customer.lastName}
                    </button>
                  </h2>
                  {customer.tags && customer.tags.length > 0 && (
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                      {customer.tags.map(tagId => {
                        const tag = tags.find(t => t.id === tagId);
                        if (!tag) return null;
                        return (
                          <span
                            key={tagId}
                            style={{
                              fontSize: '0.7rem',
                              padding: '0.15rem 0.4rem',
                              borderRadius: '20px',
                              backgroundColor: tag.color,
                              color: '#000',
                              border: `1.5px solid ${tag.color}`,
                              fontWeight: 600
                            }}
                          >
                            {tag.name}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <div className="customer-details">
                    {customer.email && <span>E-Mail: {customer.email}</span>}
                    {customer.phone && <span>Telefon: {customer.phone}</span>}
                    {(customer.address.street || customer.address.zip || customer.address.city) && (
                      <span>
                        {customer.address.street && `${customer.address.street}, `}
                        {customer.address.zip} {customer.address.city}
                      </span>
                    )}
                  </div>
                </div>
                <div className="customer-actions">
                  <button type="button" onClick={() => handleEdit(customer)} className="btn-small">
                    Bearbeiten
                  </button>
                  <button type="button" onClick={() => handleDelete(customer.id)} className="btn-small btn-danger">
                    Löschen
                  </button>
                </div>
              </div>

              {customerCars.length > 0 && (
                <div className="customer-cars">
                  <h3 
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCustomerCars(customer.id);
                    }}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Autos ({customerCars.length}) {expandedCustomers.has(customer.id) ? '▼' : '▶'}
                  </h3>
                  {expandedCustomers.has(customer.id) && (
                    <div className="cars-list">
                    {customerCars.map(car => {
                      const carRepairs = getRepairsForCar(car.id);
                      const isExpanded = expandedCars.has(car.id);
                      const totalRepairAmount = carRepairs.reduce((sum, r) => sum + (r.totalAmount || 0), 0);
                      
                      return (
                        <div key={car.id} className="car-card">
                          <div className="car-card-header">
                            <div className="car-card-info">
                              <h3>
                                <button 
                                  className="link-button" 
                                  onClick={() => navigate(`/cars/${car.id}`)}
                                  style={{ fontSize: '1em', fontWeight: 'bold' }}
                                >
                                  {car.make} {car.model} {car.year && `(${car.year})`}
                                </button>
                              </h3>
                              <div className="car-card-details">
                                <span>Kennzeichen: {car.licensePlate || '-'}</span>
                                <span>Kilometerstand: {car.mileage?.toLocaleString('de-DE') || 0} km</span>
                              </div>
                            </div>
                            <div className="car-card-actions">
                              <button type="button" onClick={() => navigate(`/cars/${car.id}/edit`)} className="btn-small">
                                Bearbeiten
                              </button>
                            </div>
                          </div>
                          
                          <div className="car-repair-summary">
                            <div className="repair-summary-stats">
                              <span>{carRepairs.length} Reparatur{carRepairs.length !== 1 ? 'en' : ''}</span>
                              {totalRepairAmount > 0 && (
                                <span className="repair-total">Gesamt: {totalRepairAmount.toFixed(2)} {currency}</span>
                              )}
                            </div>
                            {carRepairs.length > 0 && (
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleCarHistory(car.id);
                                }}
                                className="btn-small btn-secondary"
                              >
                                {isExpanded ? 'Historie ausblenden' : 'Historie anzeigen'}
                              </button>
                            )}
                          </div>

                          {isExpanded && carRepairs.length > 0 && (
                            <div className="car-repair-history">
                              <h4>Reparaturhistorie</h4>
                              <div className="repair-history-list">
                                {carRepairs.map(repair => (
                                  <div 
                                    key={repair.id} 
                                    className="repair-history-item clickable"
                                    onClick={() => navigate(`/repairs?repairId=${repair.id}`)}
                                  >
                                    <div className="repair-history-header">
                                      <div className="repair-history-date">
                                        <strong>{repair.repairNumber || '-'}</strong> - {formatDate(repair.date)}
                                        {repair.mileage && (
                                          <span style={{ marginLeft: '0.5rem', fontSize: '0.9em', color: '#666' }}>
                                            ({repair.mileage.toLocaleString('de-DE')} km)
                                          </span>
                                        )}
                                      </div>
                                      <div className="repair-history-amount">
                                        {repair.totalAmount?.toFixed(2)} {currency}
                                      </div>
                                    </div>
                                    {repair.description && (
                                      <div className="repair-history-description">
                                        {repair.description}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="car-journal-section">
                            <div className="car-journal-header">
                              <h4 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleJournal(car.id);
                                }}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                              >
                                Bemerkungen {expandedJournals.has(car.id) ? '▼' : '▶'}
                              </h4>
                              {expandedJournals.has(car.id) && (
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (showNoteForm === car.id) {
                                      setShowNoteForm(null);
                                      setNoteFormData({ note: '', mileage: '' });
                                    } else {
                                      setShowNoteForm(car.id);
                                      setNoteFormData({ note: '', mileage: car.mileage || '' });
                                    }
                                  }}
                                  className="btn-small btn-secondary"
                                >
                                  {showNoteForm === car.id ? 'Abbrechen' : 'Notiz hinzufügen'}
                                </button>
                              )}
                            </div>

                            {expandedJournals.has(car.id) && (
                              <>
                                {showNoteForm === car.id && (
                                  <div className="note-form">
                                    <div className="form-group">
                                      <label>Notiz</label>
                                      <textarea
                                        value={noteFormData.note}
                                        onChange={(e) => setNoteFormData({ ...noteFormData, note: e.target.value })}
                                        rows="3"
                                        placeholder="Notiz eingeben..."
                                      />
                                    </div>
                                    <div className="form-group">
                                      <label>Kilometerstand (optional)</label>
                                      <input
                                        type="number"
                                        value={noteFormData.mileage}
                                        onChange={(e) => setNoteFormData({ ...noteFormData, mileage: e.target.value === '' ? '' : e.target.value })}
                                        onBlur={(e) => {
                                          const val = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                                          setNoteFormData({ ...noteFormData, mileage: val });
                                        }}
                                        placeholder="Aktueller km-Stand"
                                      />
                                    </div>
                                    <div className="form-actions">
                                      <button 
                                        onClick={() => handleAddNote(car.id)}
                                        className="btn-primary btn-small"
                                        disabled={!noteFormData.note.trim()}
                                      >
                                        Speichern
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {car.notes && car.notes.length > 0 && (
                                  <div className="journal-list">
                                    {car.notes.map(note => (
                                      <div key={note.id} className="journal-item">
                                        <div className="journal-header">
                                          <div className="journal-date">
                                            {formatDate(note.date)}
                                          </div>
                                          {note.mileage && (
                                            <div className="journal-mileage">
                                              {note.mileage.toLocaleString('de-DE')} km
                                            </div>
                                          )}
                                          <button
                                            onClick={() => handleDeleteNote(car.id, note.id)}
                                            className="btn-small btn-danger"
                                            style={{ marginLeft: 'auto' }}
                                          >
                                            Löschen
                                          </button>
                                        </div>
                                        <div className="journal-note">
                                          {note.note}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {(!car.notes || car.notes.length === 0) && showNoteForm !== car.id && (
                                  <div className="journal-empty">
                                    Keine Notizen vorhanden
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  )}
                </div>
              )}
              {customerCars.length === 0 && (
                <div className="no-cars">
                  Keine Autos vorhanden
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Customers;

